import { useEntityChange } from '@/common/entityChange/EntityChangeContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { faSave, faTimes } from '@awesome.me/kit-b4fd369ea4/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Form } from '../../form/Form';
import { useEntityFromRoute } from '../../hooks/route/useEntityFromRoute';
import { useModuleFromRoute } from '../../hooks/route/useModuleFromRoute';
import { useNavState } from '../../navState/NavStateContext';
import { usePrincipal } from '../../principal/PrincipalContext';
import { useRsrc } from '../../rsrc/RsrcContext';
import { EntityUtils } from '../../utils/EntityUtils';
import { ExprUtils } from '../../utils/ExprUtils';
import { useTab } from '../entityDetail/TabContext';
import { useEntityMgr } from '../entityView/EntityMgrContext';
import { EntityFormMode } from '../enumerations/EntityFormMode';

export const EntityForm = ({
  // Optional Module and Entity. If not provided they will be grabbed from the route
  module,
  entity,

  // Optional Entity ID. If not provided it will be retrieved from navState
  entityId: entityIdProp,

  // Optional Modifications to EntityForm
  augFieldDefs
}) => {

  const { tabLabel } = useTab();

  if (tabLabel !== 'Info' && !entity) {
    throw new Error(`An Entity needs to be provided to the EntityForm in tab ${tabLabel}. This is a development error`);
  }

  const routeModule = useModuleFromRoute();
  module ??= routeModule;
  const routeEntity = useEntityFromRoute();
  entity ??= routeEntity;

  const { navState, assign } = useNavState();
  const rsrc = useRsrc();
  const { asOfDate, setExportReq, entityDef, entityFormMode, setEntityFormMode } = useEntityMgr();
  const { getFieldRoleSet } = usePrincipal();

  const [fieldDefs, setFieldDefs] = useState();
  const [entityId, setEntityId] = useState(entityIdProp ?? navState.entityId);

  const formRef = useRef();

  useEffect(() => {
    rsrc.fieldDefs(module, entity).then(fieldDefs => {

      // TODO: Map field type to form field component

      if (augFieldDefs) {
        const fieldToFieldDef = Object.fromEntries(fieldDefs.map(fieldDef => [fieldDef.field, fieldDef]));
        augFieldDefs(fieldToFieldDef);
        fieldDefs = Object.values(fieldToFieldDef);
      }

      fieldDefs = fieldDefs.filter(fieldDef => !fieldDef.checkboxSelection)
      fieldDefs = EntityUtils.filterAuthorizedFields(module, entity, fieldDefs, getFieldRoleSet);

      fieldDefs.sort((a, b) => (a.precedence || 0) - (b.precedence || 0))

      setDisabled(fieldDefs);
      setFieldDefs(fieldDefs);
    })
  }, [])

  const { refreshTrigger, subscrToEntityChange, unsubscrToEntityChange } = useEntityChange();
  useEffect(() => {
    subscrToEntityChange(module, entity, [entityId]);
    return () => {
      unsubscrToEntityChange(module, entity, [entityId]);
    };
  }, [entityId]);

  // Responsible for setting the disabled status of fieldDefs
  const setDisabled = (fieldDefs) => {
    if (!fieldDefs) return;
    fieldDefs.forEach(fieldDef => {
      if (entityFormMode === EntityFormMode.VIEW) {
        fieldDef.disabled = true;
      } else if (entityFormMode === EntityFormMode.ADD) {
        fieldDef.disabled = fieldDef.editLevel === 'NEVER';
      } else {
        fieldDef.disabled = fieldDef.editLevel === 'NEVER' || fieldDef.editLevel === 'ADD_ONLY' || (fieldDef.editLevel === 'SOR' && !formRef.current.formValues?.sor);
      }
    });
  }

  // Subscribe to the mode
  useEffect(() => {
    if (!fieldDefs) return;
    setDisabled(fieldDefs);
    setFieldDefs([...fieldDefs]); // Putting the fieldDefs in a new array reference forces an update
  }, [entityFormMode])

  const refreshForm = () => {

    const currFormRef = formRef.current;
    if (currFormRef == null) return; 

    // If adding, we use anything supplied in the navState entity onlys
    if (entityFormMode === EntityFormMode.ADD) {
      currFormRef.setFormValues(navState.entityObj);
      return;
    }

    const rowsReq = {}
    const exprs = [ExprUtils.whereParams('id', entityId)];

    // As Of Date
    if (asOfDate) {
      exprs.push(ExprUtils.fieldExpr('verStartInstant', 'IS_LESS', asOfDate))
      exprs.push(ExprUtils.fieldExpr('verEndInstant', 'IS_GREATER_OR_EQUAL', asOfDate))
      rowsReq.hist = true;
    }

    rowsReq.expr = ExprUtils.and(...exprs);

    if (navState.partition) {
      // rowsReq.partition = navState.partition;
    }

    buildAndSetExportReq(rowsReq);

    rsrc.one(module, entity, rowsReq).then(entityObj => {
      entityObj ??= {}; // TODO: What to do if asOfDate is before Entity's creation?
      currFormRef.setFormValues(entityObj);
    })
  }

  const buildAndSetExportReq = (rowsReq) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: entityDef.displayName,
      module: module,
      entity: entity,
      entityId,
      label: tabLabel,
      rowsReq,
      fieldDefs,
      uiType: 'DETAIL'
    }

    setExportReq(exportReq);
  }

  // Subscribe to the refresh button, asOfDate, and entityId change (this happens after saving a new Entity)
  useEffect(() => {
    if (fieldDefs == null) return;
    refreshForm();
  }, [refreshTrigger, asOfDate, entityId, fieldDefs]);

  const save = () => {
    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const newEntityObj = formRef.current.formValues;

    // TODO: discuss SOR

    rsrc.save(module, entity, newEntityObj).then(entityObj => {
      assign('entityId', entityObj.id);
      setEntityId(entityObj.id);
      setEntityFormMode(EntityFormMode.VIEW);
    })
  }

  const cancel = () => {
    if (entityFormMode === EntityFormMode.ADD) {
      window.history.back();
      return;
    }

    setEntityFormMode(EntityFormMode.VIEW);
    refreshForm();
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: 0 }}>
      {/* The className on this box changes the styling of disabled form fields so that in VIEW mode they are more visible */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, pb: 0 }} className={entityFormMode === EntityFormMode.VIEW ? 'entity-form-wrapper' : null}>
        <Form
          ref={formRef}
          fieldDefs={fieldDefs}
          useSections={true}
        />
      </Box>
      {entityFormMode !== EntityFormMode.VIEW && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            columnGap: '10px',
            pr: 2,
          }}
        >
          <Button sx={{ minWidth: '100px' }} onClick={cancel}>
            <Box component="span" sx={{ mr: 1.3 }}>
              <FontAwesomeIcon icon={faTimes} />
            </Box>
            Cancel
          </Button>
          <Button sx={{ minWidth: '100px' }} data-test-id="save-button" onClick={save}>
            <Box component="span" sx={{ mr: 1.3 }}>
              <FontAwesomeIcon icon={faSave} />
            </Box>
            Save
          </Button>
        </Box>
      )}
    </Box>
  )
}